import {useEffect, useRef} from "react";

import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {FillDataPopup} from "@popups/attentionPopups/FillDataPopup";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import CriteriaService from "@services/CriteriaService";
import {
  clearEdit,
  getEditCriteria,
  setError,
  setIsChanged,
  setTarget,
  updateCriteria,
} from "@redux/slices/criteriaSlices/editSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {EditTitle} from "@ui/forms/EditTitle";
import {BorderArticle} from "@ui/general/BorderArticle";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";

import {CriteriaSectionsList} from "./components/CriteriaSectionsList";
import {
  isCriteriaTargetDirty,
  normalizeCriteriaTarget,
} from "./components/criteriaGridHelpers";
import styles from "./styles.module.scss";

export const CriteriaEditPage = () => {
  const {openPopup} = usePopup();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {id} = useParams();

  const {isMobile} = useSelector((state) => state.common.settings);
  const {isChanged, target, initialTarget, status} = useSelector(
    (state) => state.criteria.edit,
  );

  const criteriaRef = useRef();

  const handleClickSave = async () => {
    if (!CriteriaService.isTargetValid(target)) {
      openPopup(<FillDataPopup />);
      return;
    }

    const resultAction = await dispatch(updateCriteria({id, target}));

    if (updateCriteria.fulfilled.match(resultAction)) {
      openPopup(<SuccessfulSavePopup title={t("popup.save_data.criteria")} />);
      dispatch(setIsChanged(false));
    } else {
      dispatch(setError("Save error!"));
    }
  };

  useEffect(() => {
    if (target && initialTarget) {
      dispatch(setIsChanged(isCriteriaTargetDirty(target, initialTarget)));
    }
  }, [target, initialTarget, dispatch]);

  useEffect(() => {
    dispatch(getEditCriteria({id}));

    return () => {
      dispatch(clearEdit());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (criteriaRef.current && isMobile) {
      criteriaRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [isMobile]);

  const normalizedTarget = target ? normalizeCriteriaTarget(target) : null;

  return (
    <div
      ref={criteriaRef}
      className={styles.criteriaWrapper}
    >
      <BorderArticle>
        <div className={styles.criteria}>
          <BackButton />

          {status === "loading" || !normalizedTarget ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.criteriaContent}>
              <div className={styles.criteriaContentHeadWrapper}>
                <div className={styles.criteriaContentHead}>
                  <EditTitle
                    label={"criteria-name"}
                    value={normalizedTarget.name}
                    placeholder={t("input.criteria_name_placeholder")}
                    onChange={(e) =>
                      dispatch(
                        setTarget({
                          ...normalizedTarget,
                          name: e.target.value,
                        }),
                      )
                    }
                    handleClickSave={handleClickSave}
                    disabledButton={!isChanged}
                  />
                </div>
              </div>

              <CriteriaSectionsList
                target={normalizedTarget}
                onChange={(nextTarget) => dispatch(setTarget(nextTarget))}
              />
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
